import { useState, useEffect, useCallback } from "react";
import api from "../../api";

// ─── API helpers ──────────────────────────────────────────────────────────────

const fetchMonthlyVerification = (bagId) =>
  api
    .get("/inventory/monthly-verification/", { params: { bag_id: bagId } })
    .then((r) => r.data);

const submitVerification = (payload) =>
  api.post("/inventory/monthly-verification/", payload).then((r) => r.data);

// ─── helpers ──────────────────────────────────────────────────────────────────

function diffColor(diff) {
  if (diff === 0) return { color: "#3B6D11", bg: "#EAF3DE" };
  if (diff > 0)  return { color: "#993C1D", bg: "#FAECE7" };
  return { color: "#185FA5", bg: "#E6F1FB" };
}

function statusPillStyle(status) {
  const map = {
    PENDING:  { color: "#7A5200", bg: "#FFF4D6" },
    VERIFIED: { color: "#3B6D11", bg: "#EAF3DE" },
    FLAGGED:  { color: "#A32D2D", bg: "#FCEBEB" },
  };
  return map[status] ?? { color: "#888", bg: "#f0f0f0" };
}

// ─── Row-level verification form ─────────────────────────────────────────────

function VerificationRow({ bagId, item, onDone }) {
  const [choice, setChoice]       = useState(null); // "correct" | "incorrect"
  const [extraQty, setExtraQty]   = useState("");
  const [remarks, setRemarks]     = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]  = useState(false);
  const [error, setError]          = useState(null);

  const isIncorrect = choice === "incorrect";

  const validate = () => {
    if (!choice) return "Please select an option.";
    if (isIncorrect) {
      if (extraQty === "" || extraQty === null) return "Extra used quantity is required.";
      if (Number(extraQty) < 0) return "Quantity cannot be negative.";
      if (!remarks.trim()) return "Remarks are required.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setSubmitting(true);

    const payload = {
      bag_id: bagId,
      inventory_item_id: item.inventory_item_id,
      is_correct: choice === "correct",
      ...(isIncorrect && {
        extra_used_quantity: Number(extraQty),
        remarks: remarks.trim(),
      }),
    };

    try {
      await submitVerification(payload);
      setSubmitted(true);
      onDone(item.inventory_item_id, choice === "correct");
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <td colSpan={2} style={vStyles.successCell}>
        <span style={vStyles.successBadge}>
          ✓ Submitted
        </span>
      </td>
    );
  }

  return (
    <td colSpan={2} style={{ padding: "10px 12px", verticalAlign: "top" }}>
      {/* choice buttons */}
      <div style={vStyles.choiceRow}>
        <button
          style={{
            ...vStyles.choiceBtn,
            ...(choice === "correct" ? vStyles.choiceBtnCorrectActive : {}),
          }}
          onClick={() => { setChoice("correct"); setError(null); }}
        >
          <span style={vStyles.choiceIcon}>✓</span> Count is Correct
        </button>
        <button
          style={{
            ...vStyles.choiceBtn,
            ...(choice === "incorrect" ? vStyles.choiceBtnIncorrectActive : {}),
          }}
          onClick={() => { setChoice("incorrect"); setError(null); }}
        >
          <span style={vStyles.choiceIcon}>✗</span> Count is Not Correct
        </button>
      </div>

      {/* extra fields */}
      {isIncorrect && (
        <div style={vStyles.extraFields}>
          <label style={vStyles.label}>
            Extra Used Quantity <span style={{ color: "#E24B4A" }}>*</span>
          </label>
          <input
            type="number"
            min="0"
            placeholder="e.g. 5"
            value={extraQty}
            onChange={(e) => { setExtraQty(e.target.value); setError(null); }}
            style={vStyles.input}
          />
          <label style={{ ...vStyles.label, marginTop: 8 }}>
            Remarks <span style={{ color: "#E24B4A" }}>*</span>
          </label>
          <textarea
            placeholder="e.g. Emergency camp usage not recorded"
            value={remarks}
            onChange={(e) => { setRemarks(e.target.value); setError(null); }}
            rows={2}
            style={vStyles.textarea}
          />
        </div>
      )}

      {/* error */}
      {error && <p style={vStyles.errorText}>{error}</p>}

      {/* submit */}
      {choice && (
        <button
          style={{
            ...vStyles.submitBtn,
            opacity: submitting ? 0.65 : 1,
            cursor: submitting ? "not-allowed" : "pointer",
          }}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Submitting…" : "Submit Verification"}
        </button>
      )}
    </td>
  );
}

// ─── main modal ───────────────────────────────────────────────────────────────

export default function MonthlyVerificationModal({ bagId, bagLabel, onClose }) {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [fetchErr,  setFetchErr]  = useState(null);
  const [doneMap,   setDoneMap]   = useState({}); // itemId → bool (is_correct)

  useEffect(() => {
    setLoading(true);
    fetchMonthlyVerification(bagId)
      .then(setData)
      .catch(() => setFetchErr("Failed to load verification data. Please try again."))
      .finally(() => setLoading(false));
  }, [bagId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleDone = useCallback((itemId, isCorrect) => {
    setDoneMap((prev) => ({ ...prev, [itemId]: isCorrect }));
  }, []);

  const items       = data?.items ?? [];
  const doneCount   = Object.keys(doneMap).length;
  const totalCount  = items.length;
  const allDone     = totalCount > 0 && doneCount >= totalCount;

  return (
    <>
      {/* backdrop */}
      <div style={mvStyles.backdrop} onClick={onClose} />

      {/* modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Monthly Bag Verification"
        style={mvStyles.modal}
      >
        {/* header */}
        <div style={mvStyles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={mvStyles.headerIcon}>📋</div>
            <div>
              <p style={mvStyles.title}>Monthly Verification</p>
              <p style={mvStyles.subtitle}>
                {bagLabel ? `Bag: ${bagLabel}` : `Bag #${bagId}`}
                {data?.technician ? ` · ${data.technician}` : ""}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* progress pill */}
            {totalCount > 0 && (
              <span style={{
                ...mvStyles.progressPill,
                background: allDone ? "#EAF3DE" : "#f0f0f0",
                color: allDone ? "#3B6D11" : "#888",
              }}>
                {doneCount}/{totalCount} verified
              </span>
            )}
            <button style={mvStyles.closeBtn} onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        {/* body */}
        <div style={mvStyles.body}>
          {loading && (
            <div style={mvStyles.centerBox}>
              <div style={mvStyles.spinner} />
              <span style={{ fontSize: 13, color: "#888" }}>Loading verification data…</span>
            </div>
          )}

          {fetchErr && (
            <div style={mvStyles.errorBox}>
              <span>⚠️</span> {fetchErr}
            </div>
          )}

          {!loading && !fetchErr && items.length === 0 && (
            <div style={mvStyles.centerBox}>
              <span style={{ fontSize: 32 }}>🗂️</span>
              <span style={{ fontSize: 13, color: "#aaa" }}>No items found for this bag.</span>
            </div>
          )}

          {!loading && !fetchErr && items.length > 0 && (
            <>
              {allDone && (
                <div style={mvStyles.allDoneBanner}>
                  ✅ All items verified for this bag.
                </div>
              )}

              <div style={mvStyles.tableWrap}>
                <table style={mvStyles.table}>
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "8%"  }} />
                    <col style={{ width: "9%"  }} />
                    <col style={{ width: "27%" }} />
                  </colgroup>
                  <thead>
                    <tr style={mvStyles.thead}>
                      {[
                        "Item Name",
                        "Assigned",
                        "Used",
                        "Remaining",
                        "Expected",
                        "Diff",
                        "Status",
                        "Verification",
                      ].map((h) => (
                        <th key={h} style={mvStyles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const dc   = diffColor(item.difference);
                      const sp   = statusPillStyle(item.status);
                      const done = doneMap[item.inventory_item_id] !== undefined;

                      return (
                        <tr
                          key={item.inventory_item_id}
                          style={{
                            ...mvStyles.row,
                            background: done ? "#f9fdf6" : "#fff",
                          }}
                        >
                          <td style={{ ...mvStyles.td, fontWeight: 600, color: "#222" }}>
                            {item.item_name}
                          </td>
                          <td style={mvStyles.td}>{item.assigned_quantity}</td>
                          <td style={mvStyles.td}>{item.used_quantity}</td>
                          <td style={{ ...mvStyles.td, fontWeight: 600, color: "#1D9E75" }}>
                            {item.remaining_quantity}
                          </td>
                          <td style={mvStyles.td}>{item.expected_remaining}</td>
                          <td style={mvStyles.td}>
                            <span style={{
                              ...mvStyles.diffBadge,
                              background: dc.bg,
                              color: dc.color,
                            }}>
                              {item.difference > 0 ? `+${item.difference}` : item.difference}
                            </span>
                          </td>
                          <td style={mvStyles.td}>
                            <span style={{
                              ...mvStyles.statusPill,
                              background: sp.bg,
                              color: sp.color,
                            }}>
                              {item.status}
                            </span>
                          </td>

                          {/* verification cell */}
                          {done ? (
                            <td style={vStyles.successCell}>
                              <span style={vStyles.successBadge}>✓ Submitted</span>
                            </td>
                          ) : (
                            <VerificationRow
                              key={item.inventory_item_id}
                              bagId={bagId}
                              item={item}
                              onDone={handleDone}
                            />
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const mvStyles = {
  backdrop: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 70,
  },
  modal: {
    position: "fixed",
    top: "50%", left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 71,
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 12px 48px rgba(0,0,0,0.2)",
    width: "min(98vw, 1160px)",
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem 1.5rem",
    borderBottom: "1px solid #f0f0f0",
    flexShrink: 0,
    gap: 8,
  },
  headerIcon: {
    width: 38, height: 38,
    background: "#EAF3DE",
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    color: "#111",
  },
  subtitle: {
    margin: "2px 0 0",
    fontSize: 12,
    color: "#999",
  },
  progressPill: {
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 99,
    transition: "all 0.2s",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 26,
    lineHeight: 1,
    cursor: "pointer",
    color: "#bbb",
    padding: "0 4px",
    flexShrink: 0,
  },
  body: {
    overflowY: "auto",
    flex: 1,
    padding: "1.25rem 1.5rem",
  },
  centerBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    padding: "3rem",
    color: "#aaa",
    fontSize: 13,
  },
  spinner: {
    width: 28, height: 28,
    border: "2.5px solid #eee",
    borderTopColor: "#1D9E75",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#FCEBEB",
    color: "#A32D2D",
    borderRadius: 8,
    padding: "0.75rem 1rem",
    fontSize: 13,
    marginBottom: 12,
  },
  allDoneBanner: {
    background: "#EAF3DE",
    color: "#3B6D11",
    fontSize: 13,
    fontWeight: 600,
    padding: "10px 14px",
    borderRadius: 8,
    marginBottom: 14,
  },
  tableWrap: {
    border: "1px solid #f0f0f0",
    borderRadius: 10,
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    tableLayout: "fixed",
  },
  thead: {
    background: "#fafaf9",
  },
  th: {
    padding: "8px 12px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 600,
    color: "#bbb",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderBottom: "1px solid #f0f0f0",
  },
  row: {
    borderBottom: "1px solid #f7f7f6",
    transition: "background 0.15s",
  },
  td: {
    padding: "10px 12px",
    color: "#333",
    verticalAlign: "middle",
  },
  diffBadge: {
    display: "inline-block",
    fontSize: 12,
    fontWeight: 700,
    padding: "2px 7px",
    borderRadius: 6,
  },
  statusPill: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    padding: "3px 7px",
    borderRadius: 99,
    letterSpacing: "0.04em",
  },
};

const vStyles = {
  choiceRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  choiceBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 12,
    fontWeight: 500,
    padding: "5px 10px",
    borderRadius: 7,
    border: "1.5px solid #e0e0e0",
    background: "#fafafa",
    color: "#555",
    cursor: "pointer",
    transition: "all 0.15s",
    whiteSpace: "nowrap",
  },
  choiceBtnCorrectActive: {
    border: "1.5px solid #1D9E75",
    background: "#EAF3DE",
    color: "#3B6D11",
    fontWeight: 600,
  },
  choiceBtnIncorrectActive: {
    border: "1.5px solid #E24B4A",
    background: "#FCEBEB",
    color: "#A32D2D",
    fontWeight: 600,
  },
  choiceIcon: {
    fontWeight: 700,
  },
  extraFields: {
    display: "flex",
    flexDirection: "column",
    marginTop: 8,
    gap: 2,
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    color: "#666",
    marginBottom: 3,
  },
  input: {
    border: "1px solid #ddd",
    borderRadius: 6,
    padding: "5px 9px",
    fontSize: 12,
    color: "#222",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  textarea: {
    border: "1px solid #ddd",
    borderRadius: 6,
    padding: "5px 9px",
    fontSize: 12,
    color: "#222",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    resize: "vertical",
    fontFamily: "inherit",
  },
  errorText: {
    margin: "6px 0 0",
    fontSize: 11,
    color: "#A32D2D",
  },
  submitBtn: {
    marginTop: 8,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    background: "#1D9E75",
    border: "none",
    borderRadius: 7,
    cursor: "pointer",
    transition: "opacity 0.15s",
  },
  successCell: {
    padding: "10px 12px",
    verticalAlign: "middle",
  },
  successBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 12,
    fontWeight: 600,
    color: "#3B6D11",
    background: "#EAF3DE",
    padding: "4px 10px",
    borderRadius: 7,
  },
};